import React, { FC, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInputPassword } from '../../inputs/FormInputPassword';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { ButtonClassNames, InputRegexPattern } from '../../../models/Common';
import { ApiError } from '../../../models/ApiError';
import { getErrorMessage } from '../../../utils';

interface SetNewPasswordFormProps {
  submitNewPassword: (password: {
    password: string;
    passwordConfirmation: string;
    currentPassword: string;
  }) => Promise<void>;
  buttonText: string;
  description?: boolean;
  helperText?: string;
  pattern?: RegExp;
  isLoading: boolean;
  handleForgotPassword?: () => void;
}

const defaultProps = {
  description: true,
  helperText: undefined,
  pattern: InputRegexPattern.LettersNumbersUnderscore,
  handleForgotPassword: () => ({}),
};

interface ISetNewPasswordForm {
  password: string;
  passwordConfirmation: string;
  currentPassword: string;
}

export const SetNewPasswordForm: FC<SetNewPasswordFormProps> = ({
  submitNewPassword,
  description,
  helperText,
  pattern,
  isLoading,
  handleForgotPassword = () => ({}),
}) => {
  const { t } = useTranslation();
  const methods = useForm<ISetNewPasswordForm>();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { handleSubmit, register, watch, setError, reset } = methods;
  const pass = useRef({});
  pass.current = watch('password', '');

  const passwordConfirmRule = {
    validate: (value: string) => value === pass.current || t('validation.passwordsDoNotMatch').toString(),
  };

  const submit = async ({ password, passwordConfirmation, currentPassword }) => {
    try {
      await submitNewPassword({ password, passwordConfirmation, currentPassword });
      reset({
        password: '',
        passwordConfirmation: '',
        currentPassword: '',
      });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof ISetNewPasswordForm, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof ISetNewPasswordForm>;
        apiErrorsKeys.forEach((element) => {
          setError(element, {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  return (
    <FormProvider {...methods}>
      {description && (
        <p className="set-new-password__text">
          <strong>{t('auth.from6To16CharactersInLatinAndNumbersOfSpecialCharactersOnlyAllowed_')} </strong>
        </p>
      )}
      <form className="set-new-password__form form" onSubmit={handleSubmit(submit)}>
        <FormInputPassword
          name="currentPassword"
          className="primary"
          label={t('auth.enterPassLk')}
          isRequired
          minLength={6}
          maxLength={16}
          fieldHasName
        />
        <FormInputPassword
          name="password"
          label={t('auth.enterNewPassword')}
          className="primary"
          // ref={(e) => register(e?.value || '')}
          helperText={helperText}
          isRequired
          pattern={pattern}
          minLength={6}
          maxLength={16}
        />
        <FormInputPassword
          name="passwordConfirmation"
          label={t('auth.repeatNewPassword')}
          className="primary"
          ref={() => register('passwordConfirmation', passwordConfirmRule)}
          isRequired
        />
        <div className="modals__btns-wrapper">
          <div className="set-new-password__form__btn-group btn-group">
            <CustomMUIButton
              label={t('buttons.save')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isLoading}
            />
          </div>
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('auth.forgotMyPassword')}
              handleClick={handleForgotPassword}
              className={ButtonClassNames.SecondaryOutlined}
              isBold
              type="button"
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
SetNewPasswordForm.defaultProps = defaultProps;
